import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import { useCartStore } from '../stores/cartStore';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';
import { usePrice } from '../hooks/usePrice';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

type PaymentMethod = 'stripe' | 'crypto';

interface Order {
  orderId: string;
  orderNumber: string;
  total: number;
}

function CheckoutForm({ order, onSuccess }: { order: Order; onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const { formatPrice } = usePrice();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard/orders/${order.orderId}`,
      },
    });

    if (error) {
      setErrorMessage(error.message || 'An error occurred');
      setIsProcessing(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {errorMessage && (
        <div className="mt-4 p-3 bg-error/10 border border-error/30 rounded text-error text-sm">
          {errorMessage}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="btn-primary w-full py-4 mt-6"
      >
        {isProcessing ? 'Processing...' : `Pay ${formatPrice(order.total)}`}
      </button>
    </form>
  );
}

function CryptoPayment({ order }: { order: Order }) {
  const [chargeData, setChargeData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const createCharge = async () => {
      try {
        const response = await api.post('/payments/crypto/create-charge', {
          orderId: order.orderId,
        }) as any;
        setChargeData(response.data);
        setIsLoading(false);
      } catch (err: any) {
        setError(err.message || 'Failed to create crypto payment');
        setIsLoading(false);
      }
    };
    createCharge();
  }, [order.orderId]);

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-text-secondary">Creating crypto payment...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-error/10 border border-error/30 rounded text-error">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-text-secondary mb-4">
          Complete your payment using cryptocurrency
        </p>
        <a
          href={chargeData.hostedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary inline-block py-4 px-8"
        >
          Pay with Crypto
        </a>
      </div>

      <div className="border-t border-gold/10 pt-6">
        <p className="text-text-muted text-sm text-center mb-4">
          Or send payment directly to one of these addresses:
        </p>
        <div className="space-y-3">
          {chargeData.addresses && Object.entries(chargeData.addresses).map(([currency, address]: [string, any]) => (
            <div key={currency} className="bg-leather rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-cream font-semibold uppercase">{currency}</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(address);
                    toast.success('Address copied!');
                  }}
                  className="text-gold text-sm hover:text-gold-bright"
                >
                  Copy
                </button>
              </div>
              <code className="text-text-secondary text-sm break-all">{address}</code>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gold/10 rounded-lg p-4 text-center">
        <p className="text-gold text-sm">
          Your order will be automatically fulfilled once the payment is confirmed on the blockchain (1 confirmation required).
        </p>
      </div>

      <Link
        to={`/dashboard/orders/${order.orderId}`}
        className="block text-center text-text-secondary hover:text-cream"
      >
        View Order Status
      </Link>
    </div>
  );
}

export default function Checkout() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const { items, total, itemCount, fetchCart, clearCart } = useCartStore();
  const { formatPrice } = usePrice();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('stripe');
  const [order, setOrder] = useState<Order | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [step, setStep] = useState<'select' | 'payment'>('select');

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    }
  }, [isAuthenticated, fetchCart]);

  // Check if user is suspended
  useEffect(() => {
    if (user?.status === 'suspended' || user?.status === 'banned') {
      toast.error('Your account is suspended. You cannot make purchases.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleCreateOrder = async () => {
    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setIsCreatingOrder(true);

    try {
      // Create order
      const orderResponse = await api.post('/orders', {
        paymentMethod,
      }) as any;

      const newOrder: Order = {
        orderId: orderResponse.data.orderId,
        orderNumber: orderResponse.data.orderNumber,
        total: orderResponse.data.total,
      };

      setOrder(newOrder);

      // If Stripe, create payment intent
      if (paymentMethod === 'stripe') {
        const intentResponse = await api.post('/payments/stripe/create-intent', {
          orderId: newOrder.orderId,
        }) as any;

        setClientSecret(intentResponse.data.clientSecret);
      }

      setStep('payment');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create order');
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handlePaymentSuccess = () => {
    clearCart();
    toast.success('Payment successful!');
    navigate(`/dashboard/orders/${order?.orderId}`);
  };

  if (!isAuthenticated) {
    return (
      <div className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-western text-4xl text-cream mb-6">Checkout</h1>
          <p className="text-text-secondary mb-8">Please login or create an account to checkout.</p>
          <p className="text-text-muted text-sm mb-6">Your cart will be saved and available after you login.</p>
          <div className="flex gap-4 justify-center">
            <Link to="/login" state={{ from: '/checkout' }} className="btn-primary">
              Login
            </Link>
            <Link to="/register" state={{ from: '/checkout' }} className="btn-secondary">
              Create Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0 && !order) {
    return (
      <div className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-western text-4xl text-cream mb-6">Checkout</h1>
          <p className="text-text-secondary mb-8">Your cart is empty.</p>
          <Link to="/products" className="btn-primary">
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-western text-4xl text-cream mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Payment Section */}
          <div className="lg:col-span-2">
            {step === 'select' && (
              <div className="card p-6">
                <h2 className="font-semibold text-cream mb-6">Select Payment Method</h2>

                <div className="space-y-4 mb-8">
                  <label
                    className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                      paymentMethod === 'stripe'
                        ? 'border-gold bg-gold/10'
                        : 'border-gold/20 hover:border-gold/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="stripe"
                      checked={paymentMethod === 'stripe'}
                      onChange={() => setPaymentMethod('stripe')}
                      className="w-5 h-5 accent-gold"
                    />
                    <div className="flex-1">
                      <div className="text-cream font-medium">Credit/Debit Card</div>
                      <div className="text-text-muted text-sm">Pay securely with Stripe</div>
                    </div>
                    <div className="text-2xl">&#128179;</div>
                  </label>

                  <label
                    className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                      paymentMethod === 'crypto'
                        ? 'border-gold bg-gold/10'
                        : 'border-gold/20 hover:border-gold/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="crypto"
                      checked={paymentMethod === 'crypto'}
                      onChange={() => setPaymentMethod('crypto')}
                      className="w-5 h-5 accent-gold"
                    />
                    <div className="flex-1">
                      <div className="text-cream font-medium">Cryptocurrency</div>
                      <div className="text-text-muted text-sm">Bitcoin, Ethereum, USDC, and more</div>
                    </div>
                    <div className="text-2xl">&#8383;</div>
                  </label>
                </div>

                <button
                  onClick={handleCreateOrder}
                  disabled={isCreatingOrder}
                  className="btn-primary w-full py-4"
                >
                  {isCreatingOrder ? 'Creating Order...' : 'Continue to Payment'}
                </button>
              </div>
            )}

            {step === 'payment' && order && (
              <div className="card p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-semibold text-cream">
                    {paymentMethod === 'stripe' ? 'Card Payment' : 'Crypto Payment'}
                  </h2>
                  <span className="text-gold font-mono">#{order.orderNumber}</span>
                </div>

                {paymentMethod === 'stripe' && clientSecret && (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance: {
                        theme: 'night',
                        variables: {
                          colorPrimary: '#d4a857',
                          colorBackground: '#1a1511',
                          colorText: '#f5efe6',
                          colorDanger: '#ef4444',
                          borderRadius: '8px',
                        },
                      },
                    }}
                  >
                    <CheckoutForm order={order} onSuccess={handlePaymentSuccess} />
                  </Elements>
                )}

                {paymentMethod === 'crypto' && (
                  <CryptoPayment order={order} />
                )}
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-8">
              <h2 className="font-semibold text-cream mb-4">Order Summary</h2>

              <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                {items.map(item => (
                  <div key={item.id} className="flex gap-3">
                    <div className="w-12 h-12 bg-leather rounded overflow-hidden flex-shrink-0">
                      {item.product.thumbnailUrl ? (
                        <img
                          src={item.product.thumbnailUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-gold/30 text-xs">OO</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-cream text-sm truncate">{item.product.name}</div>
                      <div className="text-text-muted text-xs">
                        {item.quantity} x {formatPrice(item.product.price)}
                      </div>
                    </div>
                    <div className="text-cream text-sm">
                      {formatPrice(item.product.price * item.quantity)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-gold/10 pt-4 space-y-2">
                <div className="flex justify-between text-text-secondary">
                  <span>Subtotal ({itemCount} items)</span>
                  <span>{formatPrice(total)}</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span className="text-cream font-semibold">Total</span>
                  <span className="text-gold font-bold">{formatPrice(order?.total || total)}</span>
                </div>
              </div>

              {step === 'select' && (
                <Link
                  to="/cart"
                  className="block text-center mt-6 text-text-secondary hover:text-cream transition-colors text-sm"
                >
                  &larr; Back to Cart
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
